import { AreaLoader, DropListItem, DropListItemSeparator, Icons } from '@starrez/starryui';
import { color, Text } from '@starrez/starryui9';
import { useSentry } from '@starrez/starryutils';
import { ICellRendererParams } from 'ag-grid-community';
import React, { createRef, useEffect } from 'react';
import styled from 'styled-components';
import { useEntryDirectoryEvents } from '../../context/EntryDirectoryContext';
import { CheckInOut, IRecentItemsNode, useMainDirectoryEntryWizardsQuery } from '../../graphql/useMainDirectoryEntryWizardsQuery';
import { strings } from '../../localization';
import { DropListItemText } from './DropListItemText';

declare let SEND_TO_SENTRY: boolean;

export const EntryOptions: React.FC<Partial<ICellRendererParams>> = ({ data }) => {
    const { captureException } = useSentry();
    const optionRef = createRef<HTMLButtonElement>();
    const { data: wizardsData, loading, error } = useMainDirectoryEntryWizardsQuery({ entryID: data.id.toString() });
    const { checkInEntry, checkOutEntry, addNote, runWizard, openEntryActionsPopup } = useEntryDirectoryEvents();

    const wizards = wizardsData?.allMainDirectoryEntryWizards;

    useEffect(() => {
        const optionEl = optionRef.current;

        if (optionEl) {
            const focusableEl = optionEl?.closest('ul[role="menu"]')?.querySelectorAll('[tabindex="-1"]');

            if (focusableEl?.length) {
                (focusableEl[0] as HTMLElement).focus();
            }
        }
    }, [optionRef]);

    const CheckInOutOption = ({ status }: { status: CheckInOut | undefined }) => {
        switch (status) {
            case 'SHOW_CHECK_IN':
                return (
                    <DropListItem ref={optionRef} ItemIcon={Icons.CheckIn} OnClick={() => checkInEntry(data.id)}>
                        {strings.MainDirectory_Label_CheckIn}
                    </DropListItem>
                );
            case 'SHOW_CHECK_OUT':
                return (
                    <DropListItem ref={optionRef} ItemIcon={Icons.CheckOut} OnClick={() => checkOutEntry(data.id)}>
                        {strings.MainDirectory_Label_CheckOut}
                    </DropListItem>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <DropListItem
                OnClick={() => {
                    /* do nothing */
                }}
                Title={strings.Action_Status_Loading}
            >
                <AreaLoader />
            </DropListItem>
        );
    }

    if (SEND_TO_SENTRY && error) {
        captureException(error);
    }

    return (
        <>
            {wizards?.showCheckInOut && <CheckInOutOption status={wizards.showCheckInOut} />}

            {wizards?.showAddNote && <DropListItem OnClick={() => addNote(data.id)}>{strings.MainDirectory_Label_AddNote}</DropListItem>}

            {(CheckInOutOption({ status: wizards?.showCheckInOut }) || wizards?.showAddNote) && <DropListItemSeparator />}

            <DropListItemText>
                <ItemHeader>{strings.MainDirectory_Label_RecentActions.toUpperCase()}</ItemHeader>
            </DropListItemText>

            {wizards?.recentItems.length ? (
                <>
                    {wizards?.recentItems.map((wizard: IRecentItemsNode) => {
                        return (
                            <DropListItem ref={optionRef} key={wizard.id} OnClick={() => runWizard({ entryID: data.id.toString(), ...wizard })}>
                                {wizard.description}
                            </DropListItem>
                        );
                    })}
                </>
            ) : (
                <DropListItemText>
                    <Text color={color.Grey.G70} variant="normal.regular">
                        {strings.MainDirectory_Label_NoRecentActions}
                    </Text>
                </DropListItemText>
            )}

            <DropListItemSeparator />

            <DropListItem ref={optionRef} OnClick={() => openEntryActionsPopup(data.id)}>
                {strings.MainDirectory_Label_MoreActions}
            </DropListItem>
        </>
    );
};

const ItemHeader = styled.span`
    font-size: 10px;
    font-weight: bold;
    color: ${color.Grey.G70};
`;
